import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "booking-infrastructure-drawer__close-trigger" }
const _hoisted_2 = { class: "booking-infrastructure-drawer__section" }
const _hoisted_3 = { class: "booking-infrastructure-drawer__section-content-container" }
const _hoisted_4 = { class: "booking-infrastructure-drawer__row" }
const _hoisted_5 = { class: "booking-infrastructure-drawer__row" }
const _hoisted_6 = { class: "booking-infrastructure-drawer__row" }
const _hoisted_7 = { class: "booking-infrastructure-drawer__section" }
const _hoisted_8 = { class: "booking-infrastructure-drawer__section-content-container" }
const _hoisted_9 = { class: "booking-infrastructure-drawer__row" }
const _hoisted_10 = { class: "booking-infrastructure-drawer__footer" }
const _hoisted_11 = { class: "booking-infrastructure-drawer__buttons-container" }
const _hoisted_12 = ["disabled"]

import { computed, nextTick, onMounted, reactive, ref, watch } from 'vue';
import { CloseOutlined } from '@ant-design/icons-vue';
import { message } from 'ant-design-vue';
import type { FormInstance, Rule } from 'ant-design-vue/es/form';
import FormFullNameField from '@/js/components/form/form-full-name-field.vue';
import FormPhoneField from '@/js/components/form/form-phone-field.vue';
import FormEmailField from '@/js/components/form/form-email-field.vue';
import { type FormSubjectOption } from '@/js/components/form/form-subject-field.vue';
import ConsultationRequestSuccessModal from '@/js/components/consultation-request-success-modal/consultation-request-success-modal.vue';
import { getApiUrl } from '@/js/utils/getApiUrl';
import { submitFormWithCaptcha } from '@/js/utils/submitFormWithCaptcha';
import { getRecaptchaErrorMessage } from '@/js/utils/getRecaptchaToken';
import FormPrivacyConsentField from '@/js/components/form/form-privacy-consent-field.vue';
import dayjs, { type Dayjs } from 'dayjs';
import 'dayjs/locale/ru';
import ruRu from 'ant-design-vue/es/date-picker/locale/ru_RU';

const ADDITIONAL_WISHES_MAX_LENGTH = 512;

interface BookingOfInfrastructureFormMetaItem {
  id: string;
  text: string;
}

interface BookingOfInfrastructureFormMetaResponse {
  status?: string;
  msg?: {
    clusters?: BookingOfInfrastructureFormMetaItem[];
    rooms?: (BookingOfInfrastructureFormMetaItem & { clusterId: string })[];
    format?: BookingOfInfrastructureFormMetaItem[];
    additional_services?: BookingOfInfrastructureFormMetaItem[];
    privacy?: BookingOfInfrastructureFormMetaItem[];
  };
}

interface BookingOpenParams {
  cluster?: string;
  premise?: string;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'booking-of-infrastructure',
  setup(__props, { expose: __expose }) {

const ICONS_BASE =
  process.env.NODE_ENV === 'development'
    ? '/assets/images/icons'
    : '/local/templates/dist/assets/images/icons';
const pickerLocale = ruRu;
dayjs.locale('ru');
function mapMetaToOptions(
  items: BookingOfInfrastructureFormMetaItem[] | undefined,
): FormSubjectOption[] {
  return (items ?? []).map((item) => ({ value: item.id, label: item.text }));
}

const subjectOptions = ref<FormSubjectOption[]>([]);
const clusterOptions = ref<FormSubjectOption[]>([]);
const eventFormatOptions = ref<FormSubjectOption[]>([]);
const extraServicesOptions = ref<FormSubjectOption[]>([]);
const privacyOptionId = ref<string>('');
const rooms = ref<
  (BookingOfInfrastructureFormMetaItem & {
    clusterId: string;
  })[]
>([]);

const openModal = ref<boolean>(false);
const successModalDescription = ref('');
const isSubmitting = ref(false);

const showModal = () => {
  openModal.value = true;
};

const generalFormRef = ref<FormInstance>();
const contactFormRef = ref<FormInstance>();

const initialFormState = () => ({
  cluster: '',
  premise: '',
  organizationName: '',
  eventDate: null as Dayjs | null,
  startTime: null as Dayjs | null,
  durationHours: null as number | null,
  eventFormat: '',
  participantCount: null as number | null,
  extraServicesEquipment: [] as string[],
  additionalWishes: '',
  fullName: '',
  companyName: '',
  email: '',
  phone: '',
  privacy: false,
});

const form = reactive(initialFormState());
const additionalWishesLength = computed(() => form.additionalWishes.length);

const isNotBlankString = (value: string): boolean => value?.trim().length > 0;
const isValidEmail = (value: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

const isRequiredFieldsFilled = computed(
  () =>
    isNotBlankString(form.cluster) &&
    isNotBlankString(form.premise) &&
    isNotBlankString(form.organizationName) &&
    form.eventDate !== null &&
    form.startTime !== null &&
    typeof form.durationHours === 'number' &&
    form.durationHours > 0 &&
    isNotBlankString(form.eventFormat) &&
    typeof form.participantCount === 'number' &&
    form.participantCount > 0 &&
    isNotBlankString(form.fullName) &&
    isNotBlankString(form.email) &&
    form.privacy,
);

const hasValidationErrors = computed(() => !isValidEmail(form.email));
const isSubmitDisabled = computed(
  () => isSubmitting.value || !isRequiredFieldsFilled.value || hasValidationErrors.value,
);

const rules: Record<string, Rule[]> = {
  cluster: [{ required: true, message: 'Выберите кластер' }],
  premise: [{ required: true, message: 'Выберите помещение' }],
  organizationName: [{ required: true, whitespace: true, message: 'Введите название организации' }],
  eventDate: [{ required: true, message: 'Выберите дату мероприятия' }],
  startTime: [{ required: true, message: 'Укажите время начала' }],
  durationHours: [{ required: true, message: 'Укажите продолжительность', type: 'number' }],
  eventFormat: [{ required: true, message: 'Выберите формат мероприятия' }],
  participantCount: [{ required: true, message: 'Укажите количество участников', type: 'number' }],
  extraServicesEquipment: [
    {
      type: 'array',
      message: 'Выберите хотя бы один вариант доп. услуг и оборудования',
    },
  ],
  additionalWishes: [{ whitespace: true, message: 'Заполните дополнительные пожелания' }],
  fullName: [{ required: true, message: 'Введите ваше полное имя' }],
  email: [{ required: true, message: 'Введите e-mail', type: 'email' }],
};

const open = ref<boolean>(false);

const openDrawer = (params?: BookingOpenParams): void => {
  resetFormState();

  if (params?.cluster) {
    form.cluster = params.cluster;
  }

  if (params?.premise) {
    form.premise = params.premise;
  }

  open.value = true;
};

const fetchBookingOfInfrastructureFormMeta = async () => {
  try {
    const response = await fetch(getApiUrl('/api/form/reservation/'), {
      method: 'GET',
      credentials: 'same-origin',
    });

    const result = (await response.json()) as BookingOfInfrastructureFormMetaResponse;

    if (!response.ok || result.status !== 'success') {
      throw new Error(result?.error ?? 'Не удалось загрузить данные формы');
    }

    const privacyList = result.msg?.privacy ?? [];

    clusterOptions.value = mapMetaToOptions(result.msg?.clusters);
    eventFormatOptions.value = mapMetaToOptions(result.msg?.format);
    extraServicesOptions.value = mapMetaToOptions(result.msg?.additional_services);
    privacyOptionId.value = privacyList[0]?.id ?? '';
    rooms.value = result.msg?.rooms ?? [];
  } catch {
    message.error('Не удалось загрузить данные формы');
  }
};

const premiseOptions = computed(() => {
  if (!form.cluster) {
    return [];
  }

  return mapMetaToOptions(rooms.value.filter((room) => room.clusterId === form.cluster));
});

watch(premiseOptions, (options) => {
  const exists = options.some((option) => option.value === form.premise);

  if (!exists) {
    form.premise = undefined;
  }
});

function parseNameParts(fullName: string): [string, string, string] {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);

  const lastName = parts[0] ?? '';
  const firstName = parts[1] ?? '';
  const middleName = parts.slice(2).join(' ');

  return [lastName, firstName, middleName];
}

const resetFormState = () => {
  Object.assign(form, initialFormState());
  nextTick(() => {
    generalFormRef.value?.clearValidate();
    contactFormRef.value?.clearValidate();
  });
};

const onClose = () => {
  open.value = false;
  resetFormState();
};

const onSubmit = async () => {
  if (!generalFormRef.value || !contactFormRef.value) {
    return;
  }

  try {
    await Promise.all([generalFormRef.value.validate(), contactFormRef.value.validate()]);
  } catch {
    return;
  }

  if (form.privacy && !privacyOptionId.value) {
    message.error('Не удалось определить согласие на обработку данных');
    return;
  }

  isSubmitting.value = true;

  try {
    const submitResult = await submitFormWithCaptcha({
      endpoint: getApiUrl('/api/form/reservation/'),
      buildFormData: () => {
        const body = new FormData();
        const [lastName, firstName, middleName] = parseNameParts(form.fullName);

        body.append('email', form.email);
        body.append('phone', form.phone);
        body.append('last_name', lastName);
        body.append('first_name', firstName);
        body.append('middle_name', middleName);
        body.append('cluster', form.cluster);
        body.append('room', form.premise);
        body.append('company_name', form.organizationName);
        if (form.eventDate && form.startTime && form.durationHours != null) {
          const start = dayjs(form.eventDate)
            .hour(form.startTime.hour())
            .minute(form.startTime.minute())
            .second(0);

          const end = start.add(form.durationHours, 'hour');

          body.append('datetime_start', start.format('YYYY-MM-DD HH:mm:ss'));

          body.append('datetime_end', end.format('YYYY-MM-DD HH:mm:ss'));
        }
        body.append('format', form.eventFormat);
        if (form.participantCount != null) {
          body.append('quantity', String(form.participantCount));
        }
        form.extraServicesEquipment.forEach((item) => {
          body.append('additional_services[]', item);
        });
        body.append('project_description', form.additionalWishes);
        if (form.privacy && privacyOptionId.value) {
          body.append('privacy', privacyOptionId.value);
        }

        return body;
      },
    });

    if (!submitResult.ok || !submitResult.data) {
      if (submitResult.errorCode === 'captcha-not-loaded') {
        message.error(getRecaptchaErrorMessage('captcha-not-loaded'));
        return;
      }

      if (submitResult.errorCode === 'captcha-failed') {
        message.error(getRecaptchaErrorMessage('captcha-failed'));
        return;
      }

      message.error(submitResult.message ?? 'Не удалось отправить обращение');
      return;
    }

    successModalDescription.value = submitResult.data.msg ?? '';
    onClose();
    showModal();
  } catch {
    message.error('Ошибка соединения');
  } finally {
    isSubmitting.value = false;
  }
};

onMounted(() => {
  void fetchBookingOfInfrastructureFormMeta();
});

__expose({
  open: openDrawer,
});

return (_ctx: any,_cache: any) => {
  const _component_a_select_option = _resolveComponent("a-select-option")!
  const _component_a_select = _resolveComponent("a-select")!
  const _component_a_form_item = _resolveComponent("a-form-item")!
  const _component_a_input = _resolveComponent("a-input")!
  const _component_a_date_picker = _resolveComponent("a-date-picker")!
  const _component_a_time_picker = _resolveComponent("a-time-picker")!
  const _component_a_input_number = _resolveComponent("a-input-number")!
  const _component_a_textarea = _resolveComponent("a-textarea")!
  const _component_a_form = _resolveComponent("a-form")!
  const _component_a_divider = _resolveComponent("a-divider")!
  const _component_a_drawer = _resolveComponent("a-drawer")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(_component_a_drawer, {
      open: open.value,
      "root-class-name": "booking-infrastructure-drawer",
      class: "booking-infrastructure-drawer",
      onClose: onClose
    }, {
      title: _withCtx(() => [...(_cache[16] || (_cache[16] = [
        _createElementVNode("span", { class: "booking-infrastructure-drawer__header-title" }, " Бронирование инфраструктуры ", -1)
      ]))]),
      closeIcon: _withCtx(() => [
        _createElementVNode("span", _hoisted_1, [
          _createVNode(_unref(CloseOutlined))
        ])
      ]),
      footer: _withCtx(() => [
        _createElementVNode("div", _hoisted_10, [
          _createVNode(FormPrivacyConsentField, {
            "model-value": form.privacy,
            "onUpdate:modelValue": _cache[14] || (_cache[14] = ($event: any) => ((form.privacy) = $event)),
            class: "booking-infrastructure-drawer__footer-policy"
          }, null, 8, ["model-value"]),
          _createElementVNode("div", _hoisted_11, [
            _createElementVNode("button", {
              type: "button",
              class: "btn btn-secondary btn-medium btn-cancel",
              onClick: onClose
            }, " Отмена "),
            _createElementVNode("button", {
              type: "button",
              class: "btn btn-primary btn-medium",
              onClick: onSubmit,
              disabled: isSubmitDisabled.value
            }, _toDisplayString(isSubmitting.value ? 'Отправка…' : 'Отправить'), 9, _hoisted_12)
          ])
        ])
      ]),
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_2, [
          _cache[17] || (_cache[17] = _createElementVNode("span", { class: "booking-infrastructure-drawer__section-title" }, "1. Общее", -1)),
          _createVNode(_component_a_form, {
            ref_key: "generalFormRef",
            ref: generalFormRef,
            model: form,
            rules: rules,
            layout: "vertical",
            class: "booking-infrastructure-drawer__form"
          }, {
            default: _withCtx(() => [
              _createElementVNode("div", _hoisted_3, [
                _createElementVNode("div", _hoisted_4, [
                  _createVNode(_component_a_form_item, {
                    class: "stc-ant-field-skin",
                    label: "Кластер",
                    name: "cluster",
                    required: ""
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_a_select, {
                        value: form.cluster,
                        "onUpdate:value": _cache[0] || (_cache[0] = ($event: any) => ((form.cluster) = $event)),
                        placeholder: "Выберите кластер"
                      }, {
                        default: _withCtx(() => [
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(clusterOptions.value, (opt) => {
                            return (_openBlock(), _createBlock(_component_a_select_option, {
                              key: opt.value,
                              value: opt.value
                            }, {
                              default: _withCtx(() => [
                                _createTextVNode(_toDisplayString(opt.label), 1)
                              ]),
                              _: 2
                            }, 1032, ["value"]))
                          }), 128))
                        ]),
                        _: 1
                      }, 8, ["value"])
                    ]),
                    _: 1
                  }),
                  _createVNode(_component_a_form_item, {
                    class: "stc-ant-field-skin",
                    label: "Помещение",
                    name: "premise",
                    required: ""
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_a_select, {
                        value: form.premise,
                        "onUpdate:value": _cache[1] || (_cache[1] = ($event: any) => ((form.premise) = $event)),
                        placeholder: "Выберите помещение"
                      }, {
                        default: _withCtx(() => [
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(premiseOptions.value, (opt) => {
                            return (_openBlock(), _createBlock(_component_a_select_option, {
                              key: opt.value,
                              value: opt.value
                            }, {
                              default: _withCtx(() => [
                                _createTextVNode(_toDisplayString(opt.label), 1)
                              ]),
                              _: 2
                            }, 1032, ["value"]))
                          }), 128))
                        ]),
                        _: 1
                      }, 8, ["value"])
                    ]),
                    _: 1
                  })
                ]),
                _createVNode(_component_a_form_item, {
                  class: "stc-ant-field-skin",
                  label: "Название организации",
                  name: "organizationName",
                  required: ""
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_a_input, {
                      value: form.organizationName,
                      "onUpdate:value": _cache[2] || (_cache[2] = ($event: any) => ((form.organizationName) = $event)),
                      placeholder: "Введите название организации"
                    }, null, 8, ["value"])
                  ]),
                  _: 1
                }),
                _createElementVNode("div", _hoisted_5, [
                  _createVNode(_component_a_form_item, {
                    class: "stc-ant-field-skin",
                    label: "Дата мероприятия",
                    name: "eventDate",
                    required: ""
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_a_date_picker, {
                        value: form.eventDate,
                        "onUpdate:value": _cache[3] || (_cache[3] = ($event: any) => ((form.eventDate) = $event)),
                        locale: _unref(pickerLocale),
                        format: "DD.MM.YYYY",
                        placeholder: "Выберите дату",
                        style: {"width":"100%"}
                      }, null, 8, ["value", "locale"])
                    ]),
                    _: 1
                  }),
                  _createVNode(_component_a_form_item, {
                    class: "stc-ant-field-skin",
                    label: "Время начала",
                    name: "startTime",
                    required: ""
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_a_time_picker, {
                        value: form.startTime,
                        "onUpdate:value": _cache[4] || (_cache[4] = ($event: any) => ((form.startTime) = $event)),
                        locale: _unref(pickerLocale),
                        format: "HH:mm",
                        placeholder: "Время",
                        style: {"width":"100%"}
                      }, null, 8, ["value", "locale"])
                    ]),
                    _: 1
                  }),
                  _createVNode(_component_a_form_item, {
                    class: "stc-ant-field-skin",
                    label: "Продолжительность, ч",
                    name: "durationHours",
                    required: ""
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_a_input_number, {
                        value: form.durationHours,
                        "onUpdate:value": _cache[5] || (_cache[5] = ($event: any) => ((form.durationHours) = $event)),
                        min: 1,
                        max: 744,
                        placeholder: "Часов",
                        style: {"width":"100%"}
                      }, null, 8, ["value"])
                    ]),
                    _: 1
                  })
                ]),
                _createElementVNode("div", _hoisted_6, [
                  _createVNode(_component_a_form_item, {
                    class: "stc-ant-field-skin",
                    label: "Формат мероприятия",
                    name: "eventFormat",
                    required: ""
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_a_select, {
                        value: form.eventFormat,
                        "onUpdate:value": _cache[6] || (_cache[6] = ($event: any) => ((form.eventFormat) = $event)),
                        placeholder: "Выберите формат"
                      }, {
                        default: _withCtx(() => [
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(eventFormatOptions.value, (opt) => {
                            return (_openBlock(), _createBlock(_component_a_select_option, {
                              key: opt.value,
                              value: opt.value
                            }, {
                              default: _withCtx(() => [
                                _createTextVNode(_toDisplayString(opt.label), 1)
                              ]),
                              _: 2
                            }, 1032, ["value"]))
                          }), 128))
                        ]),
                        _: 1
                      }, 8, ["value"])
                    ]),
                    _: 1
                  }),
                  _createVNode(_component_a_form_item, {
                    class: "stc-ant-field-skin",
                    label: "Кол-во участников",
                    name: "participantCount",
                    required: ""
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_a_input_number, {
                        value: form.participantCount,
                        "onUpdate:value": _cache[7] || (_cache[7] = ($event: any) => ((form.participantCount) = $event)),
                        min: 1,
                        max: 100000,
                        placeholder: "Человек",
                        style: {"width":"100%"}
                      }, null, 8, ["value"])
                    ]),
                    _: 1
                  })
                ]),
                _createVNode(_component_a_form_item, {
                  class: "stc-ant-field-skin",
                  label: "Доп. услуги и оборудование",
                  name: "extraServicesEquipment"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_a_select, {
                      value: form.extraServicesEquipment,
                      "onUpdate:value": _cache[8] || (_cache[8] = ($event: any) => ((form.extraServicesEquipment) = $event)),
                      mode: "multiple",
                      "show-search": false,
                      "allow-clear": "",
                      "show-arrow": "",
                      placeholder: "Выберите один или несколько вариантов"
                    }, {
                      default: _withCtx(() => [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(extraServicesOptions.value, (opt) => {
                          return (_openBlock(), _createBlock(_component_a_select_option, {
                            key: opt.value,
                            value: opt.value
                          }, {
                            default: _withCtx(() => [
                              _createTextVNode(_toDisplayString(opt.label), 1)
                            ]),
                            _: 2
                          }, 1032, ["value"]))
                        }), 128))
                      ]),
                      _: 1
                    }, 8, ["value"])
                  ]),
                  _: 1
                }),
                _createVNode(_component_a_form_item, {
                  class: "stc-ant-field-skin",
                  label: "Доп. пожелания",
                  name: "additionalWishes"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_a_textarea, {
                      value: form.additionalWishes,
                      "onUpdate:value": _cache[9] || (_cache[9] = ($event: any) => ((form.additionalWishes) = $event)),
                      "auto-size": { minRows: 4, maxRows: 8 },
                      maxlength: ADDITIONAL_WISHES_MAX_LENGTH,
                      placeholder: "Опишите дополнительные пожелания",
                      "show-count": ""
                    }, null, 8, ["value"])
                  ]),
                  _: 1
                })
              ])
            ]),
            _: 1
          }, 8, ["model"])
        ]),
        _createVNode(_component_a_divider, { class: "booking-infrastructure-drawer__divider" }),
        _createElementVNode("div", _hoisted_7, [
          _cache[18] || (_cache[18] = _createElementVNode("span", { class: "booking-infrastructure-drawer__section-title" }, "2. Контактное лицо", -1)),
          _createVNode(_component_a_form, {
            ref_key: "contactFormRef",
            ref: contactFormRef,
            model: form,
            rules: rules,
            layout: "vertical",
            class: "booking-infrastructure-drawer__form"
          }, {
            default: _withCtx(() => [
              _createElementVNode("div", _hoisted_8, [
                _createVNode(FormFullNameField, {
                  "model-value": form.fullName,
                  "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event: any) => ((form.fullName) = $event))
                }, null, 8, ["model-value"]),
                _createElementVNode("div", _hoisted_9, [
                  _createVNode(FormPhoneField, {
                    "model-value": form.phone,
                    "onUpdate:modelValue": _cache[11] || (_cache[11] = ($event: any) => ((form.phone) = $event)),
                    required: false
                  }, null, 8, ["model-value"]),
                  _createVNode(FormEmailField, {
                    "model-value": form.email,
                    "onUpdate:modelValue": _cache[12] || (_cache[12] = ($event: any) => ((form.email) = $event))
                  }, null, 8, ["model-value"])
                ])
              ])
            ]),
            _: 1
          }, 8, ["model"])
        ]),
        _createVNode(_component_a_divider, { class: "booking-infrastructure-drawer__mobile-divider" }),
        _createVNode(FormPrivacyConsentField, {
          "model-value": form.privacy,
          "onUpdate:modelValue": _cache[13] || (_cache[13] = ($event: any) => ((form.privacy) = $event)),
          class: "booking-infrastructure-drawer__mobile-policy"
        }, null, 8, ["model-value"])
      ]),
      _: 1
    }, 8, ["open"]),
    _createVNode(ConsultationRequestSuccessModal, {
      open: openModal.value,
      "onUpdate:open": _cache[15] || (_cache[15] = ($event: any) => ((openModal).value = $event)),
      description: successModalDescription.value
    }, null, 8, ["open", "description"])
  ], 64))
}
}

})